import { useState, useEffect, useRef } from 'react';
import { documentsApi, statsApi, documentTypesApi } from '../api';
import { useSidebarResize } from '../hooks/useSidebarResize';
import type { Document, DashboardStats, DocumentType } from '../types';
import { useSettings } from '../context/SettingsContext';
import {
  FileText, Upload, Clock,
  AlertTriangle, CheckCircle, RefreshCw, X, ExternalLink, FolderSync, Pencil, Check,
} from 'lucide-react';
import {
  Header, StatsCard, ConfidenceBar, Initials,
  TYPE_COLORS, STATUS_CONFIG,
} from '../components/ui';
import { timeAgo, fmtMs, fmtPct } from '../utils';
import { ExtractedFieldsEditor } from '../components/ExtractedFieldsEditor';

/** Formate la source du document de façon lisible */
function parseSource(source?: string): { label: string; icon: 'upload' | 'scan' | 'api' } {
  if (!source || source === 'upload') return { label: 'Import manuel', icon: 'upload' };
  if (source.startsWith('scan:')) return { label: source.slice(5), icon: 'scan' };
  if (source === 'api') return { label: 'API', icon: 'api' };
  return { label: source, icon: 'upload' };
}

function SourceBadge({ source }: { source?: string }) {
  const { label, icon } = parseSource(source);
  if (icon === 'scan') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100">
        <FolderSync className="w-3 h-3" /> {label}
      </span>
    );
  }
  if (icon === 'api') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-100">
        API
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-100">
      <Upload className="w-3 h-3" /> Import
    </span>
  );
}

export function DashboardPage() {
  const { formatDate } = useSettings();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);

  const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
  const [changingType, setChangingType] = useState(false);
  const [newTypeId, setNewTypeId] = useState('');
  const [savingType, setSavingType] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validatedIds, setValidatedIds] = useState<Set<string>>(new Set());

  const { width: sidebarWidth, handleMouseDown: handleSidebarResize, isResizing } = useSidebarResize('dashboard-sidebar-width')
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollDetailRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshList = () =>
    Promise.all([
      statsApi.dashboard(),
      documentsApi.list({ size: 10 }),
    ]).then(([sRes, dRes]) => {
      setStats(sRes.data);
      setDocuments(dRes.data.items);
      const hasPending = dRes.data.items.some((d: Document) => d.status === 'en_cours');
      if (!hasPending && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }).catch(() => {});

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => refreshList(), 4000);
  };

  useEffect(() => {
    refreshList();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollDetailRef.current) clearInterval(pollDetailRef.current);
    };
  }, []);
  useEffect(() => { documentTypesApi.list().then(r => setDocTypes(r.data)); }, []);

  const handleSaveType = (docId: string) => {
    if (!newTypeId) return;
    setSavingType(true);
    documentsApi.update(docId, { document_type_id: newTypeId })
      .then(() => { handleSelectDoc({ id: docId } as Document); refreshList(); })
      .finally(() => { setSavingType(false); setChangingType(false); setNewTypeId(''); });
  };

  const handleValidate = (docId: string) => {
    setValidating(true);
    documentsApi.validate(docId)
      .then(() => {
        setValidatedIds(prev => new Set(prev).add(docId));
        handleSelectDoc({ id: docId } as Document);
        refreshList();
      })
      .finally(() => setValidating(false));
  };

  const handleSelectDoc = (doc: Document) => {
    setLoadingDoc(true);
    documentsApi.get(doc.id)
      .then(r => setSelectedDoc(r.data))
      .finally(() => setLoadingDoc(false));
  };

  const handlePreview = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    try {
      const res = await documentsApi.getPreviewUrl(docId);
      const blob = new Blob([res.data], { type: res.headers['content-type'] as string | undefined });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      alert("Impossible d'ouvrir le document.");
    }
  };

  const handleReprocess = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    setReprocessingId(docId);
    try {
      await documentsApi.reprocess(docId);
      await refreshList();
      startPolling();
      // Garder le panneau ouvert et le mettre à jour jusqu'à fin du traitement
      if (pollDetailRef.current) clearInterval(pollDetailRef.current);
      pollDetailRef.current = setInterval(() => {
        documentsApi.get(docId).then(r => {
          setSelectedDoc(r.data);
          if (r.data.status !== 'en_cours') {
            clearInterval(pollDetailRef.current!);
            pollDetailRef.current = null;
          }
        });
      }, 3000);
    } finally {
      setReprocessingId(null);
    }
  };

  const handleUpload = (file: File) => {
    documentsApi.upload(file).then(() => refreshList());
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <Header title="Tableau de bord">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition"
          >
            <Upload className="w-4 h-4" /> Importer
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff"
            className="hidden"
            onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); }}
          />
        </Header>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              label="Documents traités"
              value={stats?.total_documents ?? '—'}
              sub={`↑ ${stats?.documents_today ?? 0} aujourd'hui`}
              icon={FileText} iconBg="bg-blue-50" iconColor="text-blue-600"
            />
            <StatsCard
              label="Classification auto"
              value={stats ? fmtPct(stats.avg_classification_confidence) : '—'}
              sub="Précision moyenne"
              icon={CheckCircle} iconBg="bg-emerald-50" iconColor="text-emerald-600" accent="text-emerald-600"
            />
            <StatsCard
              label="Temps moyen"
              value={stats ? fmtMs(stats.avg_processing_time_ms) : '—'}
              sub="par document"
              icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-600" accent="text-gray-500"
            />
            <StatsCard
              label="Alertes RGPD"
              value={stats?.active_alerts ?? '—'}
              sub="À traiter"
              icon={AlertTriangle} iconBg="bg-red-50" iconColor="text-red-600" accent="text-red-600"
            />
          </div>

          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Documents récents</h2>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    {['Document', 'Type', 'Confiance', 'Collaborateur', 'Statut', 'Actions'].map((h, i) => (
                      <th key={h} className={`py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide ${i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 ? (
                    <tr><td colSpan={6} className="py-12 text-center text-sm text-gray-400">Aucun document</td></tr>
                  ) : documents.map((doc) => {
                    const typeCode = doc.document_type?.code ?? 'autre';
                    const typeLabel = doc.document_type?.label ?? 'Autre';
                    const tc = TYPE_COLORS[typeCode] || TYPE_COLORS.autre;
                    const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.en_cours;
                    const isReprocessing = reprocessingId === doc.id;
                    return (
                      <tr
                        key={doc.id}
                        onClick={() => handleSelectDoc(doc)}
                        className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer transition"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 ${tc.bg} rounded-lg flex items-center justify-center`}>
                              <FileText className={`w-4 h-4 ${tc.text}`} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{doc.original_filename}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-xs text-gray-400">{timeAgo(doc.created_at)}</p>
                                <SourceBadge source={doc.source} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${tc.badge}`}>{typeLabel}</span>
                        </td>
                        <td className="py-3 px-4 w-36">
                          <ConfidenceBar value={doc.classification_confidence ?? 0} size="sm" />
                        </td>
                        <td className="py-3 px-4">
                          {doc.collaborateur ? (
                            <div className="flex items-center gap-2">
                              <Initials nom_complet={doc.collaborateur.nom_complet} size="sm" />
                              <span className="text-sm text-gray-700">{doc.collaborateur.nom_complet}</span>
                            </div>
                          ) : <span className="text-sm text-gray-400">—</span>}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.color}`}>{sc.label}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Retraiter */}
                            <button
                              title="Retraiter le document"
                              onClick={e => { e.stopPropagation(); handleReprocess(e, doc.id); }}
                              disabled={isReprocessing}
                              className="p-1.5 hover:bg-amber-50 rounded-lg group disabled:opacity-50"
                            >
                              <RefreshCw className={`w-4 h-4 text-gray-400 group-hover:text-amber-500 ${isReprocessing ? 'animate-spin text-amber-500' : ''}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop mobile panneau détail */}
      {(selectedDoc || loadingDoc) && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setSelectedDoc(null)}
        />
      )}

      {/* Handle resize — élément flex entre la liste et la sidebar, lg uniquement */}
      {(selectedDoc || loadingDoc) && (
        <div
          onMouseDown={handleSidebarResize}
          className="hidden lg:flex w-2 items-center justify-center cursor-col-resize group flex-shrink-0 bg-transparent hover:bg-teal-50 transition-colors"
          title="Redimensionner le panneau"
        >
          <div className={`w-0.5 h-16 rounded-full transition-colors ${isResizing ? 'bg-teal-500' : 'bg-gray-200 group-hover:bg-teal-400'}`} />
        </div>
      )}

      {/* Panneau Détail */}
      {(selectedDoc || loadingDoc) && (
        <div
          className="fixed inset-y-0 right-0 z-40 w-[92vw] sm:w-96 bg-white border-l border-gray-200 overflow-y-auto lg:static lg:inset-auto lg:shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Détail du document</h3>
            <button onClick={() => setSelectedDoc(null)} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {loadingDoc ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : selectedDoc && (() => {
            const typeCode = selectedDoc.document_type?.code ?? 'autre';
            const typeLabel = selectedDoc.document_type?.label ?? 'Autre';
            const tc = TYPE_COLORS[typeCode] || TYPE_COLORS.autre;
            const sc = STATUS_CONFIG[selectedDoc.status] || STATUS_CONFIG.en_cours;
            return (
              <>
                {/* Aperçu / Miniature */}
                <div className={`mx-5 mt-5 h-40 ${tc.bg} rounded-xl border border-gray-100 flex flex-col items-center justify-center gap-3`}>
                  <FileText className={`w-12 h-12 ${tc.text} opacity-50`} />
                  <button
                    onClick={e => handlePreview(e, selectedDoc.id)}
                    className={`flex items-center gap-1.5 text-xs font-semibold ${tc.text} hover:underline`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le document
                  </button>
                </div>

                <div className="p-5 space-y-5">
                  {/* Nom du fichier + statut */}
                  <div>
                    <p className="text-sm font-bold text-gray-900 leading-snug">{selectedDoc.original_filename}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{timeAgo(selectedDoc.created_at)}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {changingType ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <select
                            value={newTypeId}
                            onChange={e => setNewTypeId(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white"
                          >
                            <option value="">— Choisir —</option>
                            {docTypes.map(dt => (
                              <option key={dt.id} value={dt.id}>{dt.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSaveType(selectedDoc.id)}
                            disabled={!newTypeId || savingType}
                            className="p-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setChangingType(false); setNewTypeId(''); }}
                            className="p-1 hover:bg-gray-100 rounded-lg"
                          >
                            <X className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setChangingType(true); setNewTypeId(selectedDoc.document_type?.id ?? ''); }}
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${tc.badge} hover:opacity-80`}
                        >
                          {typeLabel} <Pencil className="w-3 h-3 ml-0.5" />
                        </button>
                      )}
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.color}`}>{sc.label}</span>
                      <SourceBadge source={selectedDoc.source} />
                    </div>
                  </div>

                  {/* Collaborateur */}
                  {selectedDoc.collaborateur && (
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <Initials nom_complet={selectedDoc.collaborateur.nom_complet} size="md" />
                      <div>
                        <p className="text-xs text-gray-500">Collaborateur</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedDoc.collaborateur.nom_complet}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Confiance classification */}
                  {selectedDoc.classification_confidence != null && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Fiabilité de la détection</p>
                      <ConfidenceBar value={selectedDoc.classification_confidence} />
                    </div>
                  )}

                  {/* Informations extraites — éditables */}
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Informations extraites</p>
                    <ExtractedFieldsEditor
                      docId={selectedDoc.id}
                      extractedData={selectedDoc.extracted_data ?? []}
                      onUpdated={() => handleSelectDoc(selectedDoc)}
                    />
                  </div>

                  {/* Bouton valider */}
                  {selectedDoc.status === 'a_verifier' && (() => {
                    const isValidated = validatedIds.has(selectedDoc.id);
                    return (
                      <button
                        onClick={() => handleValidate(selectedDoc.id)}
                        disabled={validating || isValidated}
                        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition text-white ${
                          isValidated
                            ? 'bg-emerald-600 cursor-default'
                            : 'bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400'
                        }`}
                      >
                        {validating
                          ? <><RefreshCw className="w-4 h-4 animate-spin" /> Validation...</>
                          : isValidated
                            ? <><Check className="w-4 h-4" /> Document validé</>
                            : <><Check className="w-4 h-4" /> Valider le document</>
                        }
                      </button>
                    );
                  })()}

                  {/* Bouton retraiter */}
                  <button
                    onClick={e => handleReprocess(e, selectedDoc.id)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                  >
                    <RefreshCw className="w-4 h-4" /> Retraiter le document
                  </button>
                </div>

                {/* Alerte RGPD */}
                {selectedDoc.date_expiration && (
                  <div className="mx-5 mb-5 bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-orange-800">Alerte RGPD</p>
                        <p className="text-xs text-orange-700 mt-1">
                          Expiration prévue le {formatDate(selectedDoc.date_expiration)}.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
